import { ref, computed } from 'vue'
import { apiClient } from '@/services/api.service'
import { ENDPOINTS } from '@/endpoints'
import type { OsmWaysResponse, OsmWayFeature, AddSegmentsResult } from '@/contracts/osm'
import type { DeleteSegmentsResult } from '@/contracts/network'

const MIN_ZOOM_FOR_OSM_SEGMENTS = 14
const MAX_SELECTION = 100

const isEditMode = ref(false)
const selectedNetworkId = ref<number | null>(null)
const osmWaysFeatures = ref<OsmWayFeature[]>([])
const selectedWayIds = ref<Set<number>>(new Set())
const selectedSegmentGids = ref<Set<number>>(new Set())
const isLoadingWays = ref(false)
const hasLoadedWays = ref(false)
const isSaving = ref(false)
const loadError = ref<string | null>(null)
const saveError = ref<string | null>(null)
const currentZoom = ref<number | null>(null)
let waysRequestId = 0

export function useNetworkEdit() {
  const canSave = computed(
    () => selectedWayIds.value.size > 0 || selectedSegmentGids.value.size > 0
  )
  const addCount = computed(() => selectedWayIds.value.size)
  const removeCount = computed(() => selectedSegmentGids.value.size)
  const atAddLimit = computed(() => selectedWayIds.value.size >= MAX_SELECTION)
  const atRemoveLimit = computed(() => selectedSegmentGids.value.size >= MAX_SELECTION)

  function enterMode(networkId: number) {
    isEditMode.value = true
    selectedNetworkId.value = networkId
    resetState()
  }

  function resetState() {
    osmWaysFeatures.value = []
    selectedWayIds.value = new Set()
    selectedSegmentGids.value = new Set()
    isLoadingWays.value = false
    hasLoadedWays.value = false
    isSaving.value = false
    loadError.value = null
    saveError.value = null
    currentZoom.value = null
  }

  function exitMode() {
    isEditMode.value = false
    selectedNetworkId.value = null
    resetState()
  }

  function setOsmWaysResponse(response: OsmWaysResponse | null) {
    osmWaysFeatures.value = response?.features ?? []
    loadError.value = null
    hasLoadedWays.value = true
  }

  function setLoadError(message: string) {
    osmWaysFeatures.value = []
    loadError.value = message
  }

  function getErrorMessage(err: unknown, fallback: string): string {
    const error = err as { response?: { data?: { message?: string } } }
    return error?.response?.data?.message ?? fallback
  }

  function toggleSetItem<T>(set: Set<T>, item: T, maxSize: number): Set<T> {
    if (set.has(item)) {
      const next = new Set(set)
      next.delete(item)
      return next
    }
    if (set.size >= maxSize) {
      return set
    }
    const next = new Set(set)
    next.add(item)
    return next
  }

  function toggleAdd(wayId: number) {
    selectedWayIds.value = toggleSetItem(selectedWayIds.value, wayId, MAX_SELECTION)
  }

  function toggleRemove(gid: number) {
    selectedSegmentGids.value = toggleSetItem(selectedSegmentGids.value, gid, MAX_SELECTION)
  }

  function setCurrentZoom(zoom: number | null) {
    currentZoom.value = zoom
  }

  async function fetchWaysInBbox(minLon: number, minLat: number, maxLon: number, maxLat: number) {
    const nid = selectedNetworkId.value
    if (!nid) return
    const requestId = ++waysRequestId
    isLoadingWays.value = true
    loadError.value = null
    try {
      const { data } = await apiClient.get<OsmWaysResponse>(
        ENDPOINTS.ways(minLon, minLat, maxLon, maxLat, nid),
        { suppressErrorToast: true }
      )
      if (requestId !== waysRequestId || selectedNetworkId.value !== nid) return
      setOsmWaysResponse(data)
    } catch (err: unknown) {
      if (requestId !== waysRequestId || selectedNetworkId.value !== nid) return
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 400) {
        setLoadError('Gebied te groot, zoom verder in om wegen te laden.')
      } else {
        setLoadError(getErrorMessage(err, 'Kon wegen niet laden.'))
      }
    } finally {
      if (requestId === waysRequestId) {
        isLoadingWays.value = false
      }
    }
  }

  async function save(): Promise<{ added: number; removed: number } | null> {
    const nid = selectedNetworkId.value
    if (!nid) return null
    saveError.value = null
    isSaving.value = true
    let removed = 0
    let added = 0
    const removeIds = Array.from(selectedSegmentGids.value)
    const addIds = Array.from(selectedWayIds.value)
    try {
      if (removeIds.length > 0) {
        const delRes = await apiClient.delete<DeleteSegmentsResult>(
          ENDPOINTS.deleteNetworkSegments(nid),
          { 
            data: { wayIds: removeIds },
            suppressErrorToast: true
          }
        )
        removed = delRes.data?.deleted ?? 0
      }
      if (addIds.length > 0) {
        const addRes = await apiClient.post<AddSegmentsResult>(
          ENDPOINTS.networkSegments(nid),
          { wayIds: addIds },
          { suppressErrorToast: true }
        )
        added = addRes.data?.added ?? 0
      }
      return { added, removed }
    } catch (err: any) {
      if (err.code && err.code === 'ERR_NETWORK') {
        return null
      }

      saveError.value = getErrorMessage(err, 'Opslaan mislukt.')
      return null
    } finally {
      isSaving.value = false
    }
  }

  return {
    isEditMode,
    selectedNetworkId,
    osmWaysFeatures,
    selectedWayIds,
    selectedSegmentGids,
    isLoadingWays,
    hasLoadedWays,
    isSaving,
    loadError,
    saveError,
    currentZoom,
    canSave,
    addCount,
    removeCount,
    atAddLimit,
    atRemoveLimit,
    MIN_ZOOM_FOR_OSM_SEGMENTS,
    MAX_SELECTION,
    enterMode,
    exitMode,
    setOsmWaysResponse,
    setLoadError,
    toggleAdd,
    toggleRemove,
    setCurrentZoom,
    fetchWaysInBbox,
    save
  }
}